import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { Person } from '../ui/Avatar';
import { MemberSelect, UserSelect, useMembers, type UserOption } from '../ui/UserSelect';
import {
  formatCountdown,
  getTicket,
  listCategories,
  patchTicket,
  priorityMatrix,
  replyToTicket,
  slaTone,
  unwatchTicket,
  watchTicket,
  TICKET_IMPACTS,
  TICKET_KINDS,
  TICKET_PRIORITIES,
  TICKET_URGENCIES,
  type TicketDetail,
  type TicketImpact,
  type TicketKind,
  type TicketPriority,
  type TicketStatus,
  type TicketUrgency,
} from './api';

/**
 * One chamado.
 *
 * Two columns: the conversation, and the state of the promise. The conversation is the work; the
 * panel on the right is what everybody else needs to know about it without reading the whole thread.
 *
 * The composer has one control that matters more than the rest — public reply or internal note — and
 * it is a visible toggle rather than a checkbox buried under an "advanced" label. An internal note
 * sent as a reply is a team's private assessment of a customer arriving in that customer's inbox,
 * and the cost of that mistake is high enough that the choice should be impossible to miss.
 */
export function TicketPage(): React.JSX.Element {
  const { ticketId = '' } = useParams();

  const ticket = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => getTicket(ticketId),
    enabled: ticketId !== '',
  });

  if (ticket.isLoading) return <Loading />;
  if (ticket.error) {
    return <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />;
  }
  if (!ticket.data) return <Loading />;

  return <Detail ticket={ticket.data} />;
}

function Detail({ ticket }: { ticket: TicketDetail }): React.JSX.Element {
  const client = useQueryClient();

  const { options: people } = useMembers();

  const categories = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: listCategories,
    staleTime: 5 * 60 * 1000,
  });

  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['ticket', ticket.id] });
    void client.invalidateQueries({ queryKey: ['tickets'] });
    void client.invalidateQueries({ queryKey: ['ticket-summary'] });
  };

  const change = useMutation({
    mutationFn: (payload: Parameters<typeof patchTicket>[1]) => patchTicket(ticket.id, payload),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="view view--wide">
      <PageHeader
        title={`#${ticket.number} · ${ticket.subject}`}
        description={ticket.requesterName ?? ticket.requesterEmail ?? t('support.noRequester')}
        actions={
          <div className="cell-inline">
            {ticket.nextStatuses.map((status) => (
              <button
                key={status}
                type="button"
                className={`button button--compact${
                  status === 'RESOLVED' ? ' button--primary' : ' button--quiet'
                }`}
                disabled={change.isPending}
                onClick={() => change.mutate({ status })}
              >
                {t(`support.move.${status}`)}
              </button>
            ))}
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="ticket">
        <div className="ticket__thread">
          <Panel>
            <Thread ticket={ticket} people={people} />
          </Panel>

          <Composer ticket={ticket} onDone={refresh} />
        </div>

        <aside className="ticket__side">
          <Panel title={t('support.sla')}>
            <SlaPanel ticket={ticket} />
          </Panel>

          <Panel title={t('support.details')}>
            <div className="stack__field">
              <span>{t('support.assignee')}</span>
              <MemberSelect
                value={ticket.assigneeId}
                disabled={change.isPending}
                placeholder={t('crm.unassigned')}
                onChange={(next) => change.mutate({ assigneeId: next })}
              />
            </div>

            <label className="stack__field">
              <span>{t('support.kind')}</span>
              <select
                className="select"
                value={ticket.kind}
                disabled={change.isPending}
                onChange={(event) => change.mutate({ kind: event.target.value as TicketKind })}
              >
                {TICKET_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {t(`support.kind.${value}`)}
                  </option>
                ))}
              </select>
            </label>

            <Classification ticket={ticket} onChange={change.mutate} busy={change.isPending} />

            <label className="stack__field">
              <span>{t('support.category')}</span>
              <select
                className="select"
                value={ticket.categoryId ?? ''}
                disabled={change.isPending}
                onChange={(event) => change.mutate({ categoryId: event.target.value || null })}
              >
                <option value="">{t('support.noCategory')}</option>
                {(categories.data?.categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>

            <dl className="facts">
              <dt>{t('crm.field.status')}</dt>
              <dd>{t(`support.status.${ticket.status}`)}</dd>

              <dt>{t('support.source')}</dt>
              <dd>{t(`support.from.${ticket.source}`)}</dd>

              {ticket.reviewDueAt !== null && (
                <>
                  <dt>{t('support.reviewDue')}</dt>
                  <dd>{formatDateTime(ticket.reviewDueAt)}</dd>
                </>
              )}

              <dt>{t('common.createdAt')}</dt>
              <dd>{formatDateTime(ticket.createdAt)}</dd>

              {ticket.satisfaction !== null && (
                <>
                  <dt>{t('support.rating')}</dt>
                  <dd>{`${ticket.satisfaction} / 5`}</dd>
                </>
              )}
            </dl>
          </Panel>

          <Panel title={t('support.watchers')}>
            <Watchers ticket={ticket} people={people} onDone={refresh} />
          </Panel>

          <Panel title={t('support.history')}>
            <ul className="home-list">
              {ticket.history.map((event) => (
                <li key={event.id}>
                  <span className="small">{t(`support.event.${event.eventType}`)}</span>
                  <span className="muted small">{formatRelative(event.occurredAt)}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

/**
 * The SLA, as two clocks rather than one verdict.
 *
 * First response and resolution are different promises kept by different actions, and a team that
 * answered in four minutes and has not fixed it in two days is doing one of them well. A single
 * "SLA: late" would hide which.
 */
function SlaPanel({ ticket }: { ticket: TicketDetail }): React.JSX.Element {
  const legs = [
    {
      key: 'firstResponse' as const,
      label: t('support.firstResponse'),
      health: ticket.sla.firstResponse,
      minutes: ticket.sla.minutesToFirstResponse,
      dueAt: ticket.sla.firstResponseDueAt,
      metAt: ticket.firstResponseAt,
    },
    {
      key: 'resolution' as const,
      label: t('support.resolution'),
      health: ticket.sla.resolution,
      minutes: ticket.sla.minutesToResolution,
      dueAt: ticket.sla.resolutionDueAt,
      metAt: ticket.resolvedAt,
    },
  ];

  return (
    <div className="sla">
      {legs.map((leg) => (
        <div key={leg.key} className="sla__leg">
          <span className="sla__label">{leg.label}</span>

          {leg.health === 'NONE' ? (
            <span className="muted small">{t('support.sla.NONE')}</span>
          ) : (
            <>
              <Badge tone={slaTone(leg.health)}>{t(`support.sla.${leg.health}`)}</Badge>
              <span className="muted small">
                {leg.metAt
                  ? formatDateTime(leg.metAt)
                  : leg.minutes !== null
                    ? formatCountdown(leg.minutes)
                    : leg.dueAt
                      ? formatDateTime(leg.dueAt)
                      : '—'}
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function Thread({
  ticket,
  people,
}: {
  ticket: TicketDetail;
  people: readonly UserOption[];
}): React.JSX.Element {
  if (ticket.messages.length === 0) return <p className="muted">{t('support.noMessages')}</p>;

  /*
   * Who said it, with their face.
   *
   * The customer has no account here and so no picture; they get initials from the word "Cliente",
   * which is honest — inventing a silhouette for them would make them look like a colleague.
   */
  const author = (type: string, id: string | null): { name: string; url?: string | null } => {
    if (type === 'CUSTOMER') return { name: t('support.customer') };
    if (type === 'SYSTEM') return { name: t('support.system') };

    const found = id === null ? undefined : people.find((person) => person.id === id);

    return found ? { name: found.name, url: found.avatarUrl } : { name: t('support.agent') };
  };

  return (
    <ol className="ticket-thread">
      {ticket.messages.map((message) => (
        <li
          key={message.id}
          className={`ticket-thread__item ticket-thread__item--${message.authorType.toLowerCase()}${
            message.isPublic ? '' : ' ticket-thread__item--private'
          }`}
        >
          <div className="ticket-thread__head">
            <Person size="xs" {...author(message.authorType, message.authorId)} />
            {!message.isPublic && <Badge tone="warning">{t('support.internal')}</Badge>}
            <span className="muted small">{formatRelative(message.createdAt)}</span>
          </div>

          <p className="ticket-thread__body">{message.body}</p>
        </li>
      ))}
    </ol>
  );
}

/** Answering and moving the ticket on, in one gesture. */
function Composer({
  ticket,
  onDone,
}: {
  ticket: TicketDetail;
  onDone: () => void;
}): React.JSX.Element | null {
  const [body, setBody] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [then, setThen] = useState<TicketStatus | ''>('');
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () =>
      replyToTicket(ticket.id, {
        body: body.trim(),
        isPublic,
        ...(then ? { status: then } : {}),
      }),
    onSuccess: () => {
      setBody('');
      setThen('');
      setError(null);
      onDone();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  // A closed ticket takes no more replies; showing the box would be an invitation to a 422.
  if (ticket.status === 'CLOSED' || ticket.status === 'CANCELED') {
    return (
      <Panel>
        <p className="muted">{t('support.ticketClosed')}</p>
      </Panel>
    );
  }

  return (
    <Panel>
      <div className="ticket-composer">
        <div className="ticket-composer__modes">
          <button
            type="button"
            className={`chip${isPublic ? ' chip--active' : ''}`}
            onClick={() => setIsPublic(true)}
          >
            {t('support.publicReply')}
          </button>
          <button
            type="button"
            className={`chip${isPublic ? '' : ' chip--active'}`}
            onClick={() => setIsPublic(false)}
          >
            {t('support.internalNote')}
          </button>
        </div>

        <textarea
          rows={5}
          value={body}
          placeholder={isPublic ? t('support.replyPlaceholder') : t('support.notePlaceholder')}
          maxLength={20_000}
          onChange={(event) => setBody(event.target.value)}
        />

        {error && (
          <p className="alert small" role="alert">
            {error}
          </p>
        )}

        <div className="ticket-composer__actions">
          <label className="cell-inline">
            <span className="muted small">{t('support.andThen')}</span>
            <select
              className="select"
              value={then}
              onChange={(event) => setThen(event.target.value as TicketStatus | '')}
            >
              <option value="">{t('support.keepStatus')}</option>
              {ticket.nextStatuses.map((status) => (
                <option key={status} value={status}>
                  {t(`support.status.${status}`)}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="button button--primary button--compact"
            disabled={body.trim() === '' || send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? t('support.sending') : t('support.send')}
          </button>
        </div>
      </div>
    </Panel>
  );
}

/**
 * Urgency, impact and the priority they produce (ITIL).
 *
 * The priority is shown as a consequence, not as a field somebody fills in: change either half and
 * the server recomputes it. Typing a priority directly is still allowed — some chamados are
 * political — but it *pins* the value, and the screen says so, with one control to hand it back to
 * the matrix. A priority that silently ignores the urgency it was derived from is how a support
 * queue ends up sorted by nothing in particular.
 *
 * The matrix itself is fetched rather than duplicated here, so the preview under the selects is the
 * same table the server will apply.
 */
function Classification({
  ticket,
  onChange,
  busy,
}: {
  ticket: TicketDetail;
  onChange: (payload: Parameters<typeof patchTicket>[1]) => void;
  busy: boolean;
}): React.JSX.Element {
  const matrix = useQuery({
    queryKey: ['priority-matrix'],
    queryFn: priorityMatrix,
    staleTime: 60 * 60 * 1000,
  });

  const derived = matrix.data?.matrix[ticket.urgency]?.[ticket.impact] ?? null;

  return (
    <>
      <label className="stack__field">
        <span>{t('support.urgency')}</span>
        <select
          className="select"
          value={ticket.urgency}
          disabled={busy}
          onChange={(event) => onChange({ urgency: event.target.value as TicketUrgency })}
        >
          {TICKET_URGENCIES.map((value) => (
            <option key={value} value={value}>
              {t(`support.urgency.${value}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="stack__field">
        <span>{t('support.impact')}</span>
        <select
          className="select"
          value={ticket.impact}
          disabled={busy}
          onChange={(event) => onChange({ impact: event.target.value as TicketImpact })}
        >
          {TICKET_IMPACTS.map((value) => (
            <option key={value} value={value}>
              {t(`support.impact.${value}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="stack__field">
        <span className="cell-inline">
          {t('support.priority')}
          {ticket.priorityLocked && <Badge tone="warning">{t('support.priorityPinned')}</Badge>}
        </span>
        <select
          className="select"
          value={ticket.priority}
          disabled={busy}
          onChange={(event) =>
            /*
             * `escalate` only shortens the clock, never lengthens it (domain/tickets.ts).
             * Sending it on every change is safe and means an escalation does what somebody
             * escalating expects, without a second control nobody would find.
             */
            onChange({ priority: event.target.value as TicketPriority, escalate: true })
          }
        >
          {TICKET_PRIORITIES.map((value) => (
            <option key={value} value={value}>
              {t(`support.priority.${value}`)}
            </option>
          ))}
        </select>

        {ticket.priorityLocked ? (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={busy}
            onClick={() => onChange({ priorityLocked: false })}
          >
            {t('support.useMatrix')}
            {derived !== null && ` (${t(`support.priority.${derived}`)})`}
          </button>
        ) : (
          <span className="muted small">{t('support.priorityFromMatrix')}</span>
        )}
      </label>
    </>
  );
}

/**
 * Observers: the people who need to know what happens without being the one answering.
 *
 * Adding somebody here shows them the whole chamado, internal notes included, which is why it is
 * behind the same permission as any other change to it and why the list is visible rather than
 * implicit. A colleague who can read the team's private assessment of a customer should appear on
 * the screen that assessment was written on.
 */
function Watchers({
  ticket,
  people,
  onDone,
}: {
  ticket: TicketDetail;
  people: readonly UserOption[];
  onDone: () => void;
}): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (userId: string) => watchTicket(ticket.id, userId),
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => unwatchTicket(ticket.id, userId),
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const busy = add.isPending || remove.isPending;

  // Somebody already following is not offered again; neither is the assignee, who is already on it.
  const candidates = people.filter(
    (person) => !ticket.watcherIds.includes(person.id) && person.id !== ticket.assigneeId,
  );

  return (
    <div className="stack">
      {ticket.watcherIds.length === 0 ? (
        <p className="muted small">{t('support.noWatchers')}</p>
      ) : (
        <ul className="home-list">
          {ticket.watcherIds.map((userId) => {
            const person = people.find((candidate) => candidate.id === userId);

            return (
              <li key={userId}>
                <Person
                  size="xs"
                  name={person?.name ?? t('support.agent')}
                  url={person?.avatarUrl}
                />
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={busy}
                  onClick={() => remove.mutate(userId)}
                >
                  {t('support.unwatch')}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <UserSelect
        value={null}
        options={candidates}
        allowEmpty={false}
        disabled={busy}
        placeholder={t('support.addWatcher')}
        onChange={(next) => {
          if (next) add.mutate(next);
        }}
      />

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
